import { auth, getFirebaseApp, IUser } from '@/constants'
import { useEffect, useState } from 'react'
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth'
import { apiFetch } from '@/utils'
import { useToast } from '@/hooks/use-toast'
import { useWallet } from '@/hooks/use-wallet'
import { useWalletBalance } from '@/hooks/use-balance'
import { UserContext } from '@/hooks/use-user'
import { Loader2 } from 'lucide-react'
import { useAuthorizeUserWithToken } from '@/hooks/use-authorize-with-token'
import { useLocation } from 'wouter'
import { useCheckNetworkChainId } from '@/hooks/use-check-network-chain-id'
import SkeletonWalletLoader from '@/components/ui/skeleton-wallet-loader'

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<IUser | null>(null)
  const [loading, setLoading] = useState(true)

  const { toast } = useToast()
  const [location, navigate] = useLocation()
  const {
    walletAddress,
    connectionStatus,
    error,
    isConnected,
    isConnectingWallet,
    connectWallet,
    disconnectWallet
  } = useWallet()

  const computedWalletAddress = walletAddress || user?.walletAddress

  const { balance, balanceLoading, updateBalance } = useWalletBalance({
    walletAddress: computedWalletAddress,
    userInfoLoading: loading || isConnectingWallet
  })

  const setUserData = (data: IUser | null) => {
    if (data === null) {
      setUser(null)
    } else {
      setUser((prev) => ({ ...prev, ...data }))
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (!authUser?.email) {
        setLoading(false)
        return
      }

      if (authUser?.emailVerified) {
        const { uid, email, displayName, emailVerified } = authUser

        const fetchResponse = await apiFetch(`/v1/users/${uid}`, {
          method: 'GET',
          withCredentials: true
        })

        if (!fetchResponse.ok) {
          const errorData = await fetchResponse.json()
          toast({
            title: errorData.title || 'Error',
            description: `Failed to fetch user info. ${errorData.description}`
          })
          setUser(null)
          setLoading(false)
          return
        }

        const userFetchResult = await fetchResponse.json()
        const userInfo = userFetchResult?.data?.user

        setUser({
          uid,
          email,
          displayName,
          walletAddress: userInfo.walletAddress,
          id: userInfo.id,
          emailVerified: emailVerified ?? false,
          role: userInfo.role,
          profileImageUrl: userInfo.profileImageUrl,
          profileImageOriginalUrl: userInfo.profileImageOriginalUrl
        } as IUser)
      } else {
        console.log('Redirect back to login from context')
        const unverifiedEmail = authUser.email
        setUser(null)
        document.cookie = `session_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        await signOut(auth)
        navigate(`/login?unverified=${encodeURIComponent(unverifiedEmail || '')}`)
      }
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    if (user) {
      const auth = getAuth(getFirebaseApp())

      if (!auth?.currentUser?.uid) return
      // When user on the login or signUp screen and auth was successful redirect to the home page
      // This condition prevents redirect to home page when user authorized and reloaded page
      if (['/login', '/signup'].includes(location)) {
        navigate('/')
      }
    }
  }, [user, location])

  // Tracks cookie updates and authorize user when token passed from
  // mobile app through webview
  const initialMobileAuthLoading = useAuthorizeUserWithToken(
    user?.uid,
    loading,
    setLoading,
    disconnectWallet
  )

  useCheckNetworkChainId()

  return (
    <UserContext.Provider
      value={{
        ...user,
        loading,
        walletAddress: walletAddress || user?.walletAddress,
        connectionStatus,
        walletError: error,
        isConnectingWallet,
        isConnected,
        balance,
        balanceLoading,
        setUserData,
        setUserDataLoading: setLoading,
        connectWallet,
        disconnectWallet,
        updateBalance
      }}>
      {loading || initialMobileAuthLoading ?(location === '/' ? <SkeletonWalletLoader /> : (
        <div className="flex flex-row min-h-auto items-center justify-center bg-[#18181B] h-full">
          <div className="flex flex-row items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#EF4444]" />
            <p className="text-md text-[#BABABB]">
              Fetching user data...
            </p>
          </div>
        </div>
      )) : (
        children
      )}
    </UserContext.Provider>
  )
}
